/**
 * P2-EDGE / CLO-S9 — Edge lease / proof / reconnect reconcile Soft≠ invent.
 * Tenant CMC Machine Deck — signed CROC unit action budget.
 * Never Live UAV. Not flight. Soft fleet stays Class-C localhost.
 * Honesty: Not Live UAV · No durable lease for this unit
 */
import { useEffect, useState } from "react";
import { html, KV } from "./ui.js";
import { path, useAction, api } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";

function safeEdgePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    /* contract lag fallback — Soft≠ invent */
  }
  if (op === "post__ai_edge_leases" || op === "get__ai_edge_leases") return "/ai/edge/leases";
  if (op === "get__ai_edge_leases_p") {
    const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
    return `/ai/edge/leases/${encodeURIComponent(id)}`;
  }
  if (op === "post__ai_edge_leases_p_reconcile") {
    const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
    return `/ai/edge/leases/${encodeURIComponent(id)}/reconcile`;
  }
  if (op === "post__ai_edge_leases_p_proof") {
    const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
    return `/ai/edge/leases/${encodeURIComponent(id)}/proof`;
  }
  if (op === "post__ai_edge_leases_p_consume") {
    const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
    return `/ai/edge/leases/${encodeURIComponent(id)}/consume`;
  }
  return "";
}

export function EdgeLeasePanel({ deviceId, toast }) {
  const did = String(deviceId || "").trim();
  const { run, busy, envelope } = useAction();
  const [lease, setLease] = useState(null);
  const [proofN, setProofN] = useState(0);
  const [hydrating, setHydrating] = useState(false);
  const [budget, setBudget] = useState("10");
  const [ttl, setTtl] = useState("600");

  const applyPack = (out) => {
    if (!out) return;
    if (out.lease) setLease(out.lease);
    const n = (out.proof_n != null)
      ? out.proof_n
      : ((out.proofs && out.proofs.length) || 0);
    if (out.lease || out.proofs) setProofN(n);
    if (out.budget_remaining != null && lease) {
      setLease({ ...lease, budget_remaining: out.budget_remaining });
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!did) {
      setLease(null);
      setProofN(0);
      setHydrating(false);
      return undefined;
    }
    setHydrating(true);
    const url = safeEdgePath("get__ai_edge_leases") + "?device_id=" + encodeURIComponent(did);
    api(url, { method: "GET" })
      .then((out) => {
        if (cancelled) return;
        const items = (out && out.items) || [];
        const active = items.find((row) => String(row.status || "") === "active") || items[0] || null;
        setLease(active);
        setProofN(0);
        if (active && active.lease_id) {
          const recUrl = safeEdgePath("get__ai_edge_leases_p", { pathParams: [active.lease_id] });
          return api(recUrl, { method: "GET" }).then((rec) => {
            if (cancelled) return;
            if (rec && rec.lease) setLease(rec.lease);
            setProofN((rec && rec.proofs && rec.proofs.length) || 0);
          });
        }
        return undefined;
      })
      .catch(() => {
        if (!cancelled) {
          setLease(null);
          setProofN(0);
        }
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => { cancelled = true; };
  }, [did]);

  const mint = async () => {
    if (!did) {
      toast && toast("Pick a unit first", "warn");
      return;
    }
    try {
      const url = safeEdgePath("post__ai_edge_leases");
      const out = await run(url, {
        method: "POST",
        body: {
          device_id: did,
          budget_actions: Math.max(1, parseInt(budget, 10) || 10),
          ttl_s: Math.max(60, parseInt(ttl, 10) || 600),
        },
      });
      if (!out || !out.ok) {
        toast && toast("Could not mint edge lease", "bad");
        return;
      }
      setLease(out.lease || null);
      setProofN(0);
      toast && toast("Action budget started", "go");
    } catch {
      toast && toast("Could not start an action budget", "bad");
    }
  };

  const reconcile = async () => {
    const lid = lease && lease.lease_id;
    if (!lid) {
      toast && toast("This board has no action budget yet", "warn");
      return;
    }
    try {
      const url = safeEdgePath("post__ai_edge_leases_p_reconcile", { pathParams: [lid] });
      const out = await run(url, { method: "POST", body: {} });
      if (out && out.ok) {
        applyPack(out);
        toast && toast("Budget refreshed · proofs " + ((out.proofs && out.proofs.length) || 0), "go");
      } else {
        toast && toast("Could not refresh this budget", "bad");
      }
    } catch {
      toast && toast("Could not refresh this budget", "bad");
    }
  };

  const consume = async () => {
    const lid = lease && lease.lease_id;
    if (!lid) {
      toast && toast("This board has no action budget yet", "warn");
      return;
    }
    try {
      const url = safeEdgePath("post__ai_edge_leases_p_consume", { pathParams: [lid] });
      const out = await run(url, { method: "POST", body: { n: 1 } });
      if (out && out.ok) {
        setLease({ ...lease, budget_remaining: out.budget_remaining });
        toast && toast("Used one action · remaining " + out.budget_remaining, "go");
      } else {
        toast && toast("Could not use an action", "bad");
      }
    } catch {
      toast && toast("Could not use an action", "bad");
    }
  };

  const appendProof = async () => {
    const lid = lease && lease.lease_id;
    if (!lid) {
      toast && toast("This board has no action budget yet", "warn");
      return;
    }
    try {
      const url = safeEdgePath("post__ai_edge_leases_p_proof", { pathParams: [lid] });
      const out = await run(url, {
        method: "POST",
        body: { trust_grade: "observed", kind: "reconnect", is_live_uav: false },
      });
      if (out && out.ok) {
        setProofN((n) => n + 1);
        toast && toast("Proof saved. It is a receipt, not a full check.", "go");
      } else {
        toast && toast("Proof refused", "bad");
      }
    } catch {
      toast && toast("Proof refused", "bad");
    }
  };

  const remaining = lease && lease.budget_remaining != null ? lease.budget_remaining : "—";
  const statusLine = hydrating
    ? "Reading this board’s action budget…"
    : (lease
      ? ("Budget on this board · " + remaining + " actions left · proofs " + proofN)
      : "This board has no action budget yet");

  return html`<div className="edge-lease-panel" data-cmc-edge="1" data-cmc-edge-s9="1"
      data-soft-invent="0" data-is-live-uav="0">
    <p className="note">
      How many signed actions this board may still take. This is not a drone.
      Ground robots on this site only. Refresh uses the saved record.
    </p>
    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}
    <p className="note" data-cmc-edge-status=${lease ? "leased" : "empty"}>${statusLine}</p>
    <div className="btn-row" style=${{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <label className="note">Actions
        <input type="number" min="1" value=${budget}
          onInput=${(e) => setBudget(e.target.value)} style=${{ width: 72, marginLeft: 6 }} />
      </label>
      <label className="note">Lasts (seconds)
        <input type="number" min="60" value=${ttl}
          onInput=${(e) => setTtl(e.target.value)} style=${{ width: 88, marginLeft: 6 }} />
      </label>
      <button type="button" className="btn btn--ghost btn--sm" data-cmc-edge-mint="1"
        disabled=${busy || !did} onClick=${mint}>Start budget</button>
      <button type="button" className="btn btn--ghost btn--sm" data-cmc-edge-reconcile="1"
        disabled=${busy || !(lease && lease.lease_id)} onClick=${reconcile}>Refresh budget</button>
      <button type="button" className="btn btn--ghost btn--sm" data-cmc-edge-consume="1"
        disabled=${busy || !(lease && lease.lease_id)} onClick=${consume}>Use one action</button>
      <button type="button" className="btn btn--ghost btn--sm" data-cmc-edge-proof="1"
        disabled=${busy || !(lease && lease.lease_id)} onClick=${appendProof}>Add a proof</button>
    </div>
  </div>`;
}

export default EdgeLeasePanel;
